<template>
  <v-dialog
    v-model="dialog"
    max-width="500px"
    persistent
  >
    <template #activator="{ props }">
      <v-btn
        v-if="!immutableSalaryClaimsCollection"
        rounded
        size="small"
        class="primary-inverted-button"
        v-bind="props"
        :disabled="!enableAddNew"
      >
        <v-icon
          tag="span"
          start
        >
          mdi-plus
        </v-icon>
        {{ $t("salaryPanelStrings.labels.addButton") }}
      </v-btn>
      <p
        v-else
        class="font-italic"
      >
        {{ $t("salaryPanelStrings.additionDisabled") }}
      </p>
    </template>
    <v-card>
      <v-card-title>
        {{ formTitle }}
      </v-card-title>
      <v-card-text>
        <vee-form ref="salaryObserver">
          <v-container>
            <v-row>
              <v-col
                v-if="!currentItem.readOnly"
                cols="12"
                md="6"
              >
                <claim-date-picker
                  :key="currentIndex"
                  :claim="currentItem"
                  :label="$t('salaryPanelStrings.tableHeaders.date')"
                  :allowed-dates="
                    (x) => m(x).isSameOrBefore(m(), 'days')
                  "
                />
              </v-col>
              <v-col
                v-if="!currentItem.readOnly"
                cols="12"
                md="6"
              >
                <claim-type-availability-label
                  :claim="currentItem"
                  configuration-type-property="rateType"
                  model-binding-property="claimTypeId"
                  :filter-enabled-types-func="filterEnabledSalaryTypes"
                  resources-property="enums.salaryTypes"
                  claim-resource="enums.salaryTypes"
                  :permitted-rate-type="permittedSalaryType"
                />
              </v-col>
              <v-col cols="12">
                <paying-orgs-picker
                  :claim="currentItem"
                  type-property="rateType"
                  configuration-type-property="rateType"
                  claim-type-property="claimTypeId"
                  :filter-enabled-types-func="filterEnabledSalaryTypes"
                  :validation-name="
                    $t('salaryPanelStrings.tableHeaders.payingOrgs')
                  "
                  :label="$t('salaryPanelStrings.tableHeaders.payingOrgs')"
                />
              </v-col>
              <v-col
                v-if="!currentItem.readOnly"
                cols="12"
                md="6"
              >
                <vee-field
                  v-slot="{ errors, componentField }"
                  v-model="currentItem.salaryCount"
                  rules="required|integer|min_value:1"
                  :name="$t('salaryPanelStrings.tableHeaders.count')"
                >
                  <integer-text-field
                    v-bind="componentField"
                    :key="currentIndex"
                    name="claimCount"
                    :error-messages="errors"
                    :label="$t('salaryPanelStrings.tableHeaders.count')"
                    @input="updateClaimAmount(currentItem)"
                  />
                </vee-field>
              </v-col>
              <v-col
                v-if="!currentItem.readOnly"
                cols="12"
                md="6"
              >
                <vee-field
                  v-slot="{ componentField, errors }"
                  v-model="currentItem.salaryCost"
                  rules="required|decimal:2|min_value:0.01"
                  :name="$t('salaryPanelStrings.tableHeaders.salaryCost')"
                >
                  <decimal-text-field
                    v-bind="componentField"
                    :key="currentIndex"
                    name="claimAmount"
                    :error-messages="errors"
                    :label="$t('salaryPanelStrings.tableHeaders.salaryCost')"
                    :pad-decimals="2"
                    @input="updateClaimAmount(currentItem)"
                  />
                </vee-field>
              </v-col>
              <v-col
                v-if="!currentItem.readOnly"
                cols="12"
              >
                <vee-field
                  v-slot="{ componentField, errors }"
                  v-model="currentItem.comment"
                  rules="max:200"
                  :name="$t('salaryPanelStrings.tableHeaders.comment')"
                >
                  <v-text-field
                    v-bind="componentField"
                    :error-messages="errors"
                    :label="$t('salaryPanelStrings.tableHeaders.comment')"
                    counter="200"
                    variant="outlined"
                    density="compact"
                  />
                </vee-field>
              </v-col>
              <v-col
                cols="12"
                md="6"
              >
                <amount-label
                  :value="currentItem.amount"
                  :label="$t('salaryPanelStrings.tableHeaders.amount')"
                />
              </v-col>
            </v-row>
          </v-container>
        </vee-form>
      </v-card-text>

      <v-card-actions class="pa-5 flex-column flex-sm-row">
        <v-spacer />
        <v-btn
          rounded
          :block="xs"
          class="primary-inverted-button mb-2 mb-sm-0"
          size="small"
          @click="cancel"
        >
          {{ $t("salaryPanelStrings.labels.cancelButton") }}
        </v-btn>
        <v-btn
          rounded
          :block="xs"
          class="primary-button ml-0 ml-sm-2"
          size="small"
          @click="save"
        >
          {{ saveLabel }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { Form as VeeForm, Field as VeeField } from "vee-validate";
import validationErrors from "@/mixins/validationErrors";
import ClaimDatePicker from "../components/ClaimDatePicker";
import ClaimTypeAvailabilityLabel from "../components/ClaimTypeAvailabilityLabel";
import PayingOrgsPicker from "../components/PayingOrgsPicker";
import AmountLabel from "@/views/common/components/AmountLabel";
import IntegerTextField from "@/views/common/components/IntegerTextField";
import DecimalTextField from "@/views/common/components/DecimalTextField";
import { calculateSalaryClaimTotal } from "@/utils/claims/salary";
import SalaryTypes from '../../../../enums/SalaryTypes';
import { useDisplay } from 'vuetify';
import { m } from '@/plugins/moment';

export default {
  name: "SalaryEditDialog",
  components: {
    VeeForm,
    VeeField,
    ClaimDatePicker,
    ClaimTypeAvailabilityLabel,
    PayingOrgsPicker,
    AmountLabel,
    IntegerTextField,
    DecimalTextField
  },
  mixins: [validationErrors],
  props: {
    claims: {
      default: () => [],
      type: Array
    },
    editableSalaryClaimPayingOrgs: {
      required: true
    },
    immutableSalaryClaimsCollection: {
      required: true
    },
    currentItem: {
      required: true
    },
    currentIndex: {
      required: true
    },
    enableAddNew:{
      required: true,
      type: Boolean
    }
  },
  setup() {
    const { xs } = useDisplay();
    return { xs, m };
  },
  data() {
    return {
      dialog: false
    };
  },
  computed: {
    saveLabel() {
      if (this.currentIndex < 0) {
        return this.$t("salaryPanelStrings.labels.insertButton");
      }

      return this.$t("salaryPanelStrings.labels.updateButton");
    },
    formTitle() {
      if (this.currentIndex < 0) {
        return this.$t("salaryPanelStrings.labels.insertEditDialogTitle");
      }

      return this.$t("salaryPanelStrings.labels.updateEditDialogTitle");
    },
    permittedSalaryType() {
      return SalaryTypes.Default;
    }
  },
  watch: {
    currentIndex(val) {
      if (val >= 0) {
        this.dialog = true;
      }
    },
    dialog(val) {
      if (val && this.currentIndex < 0 && this.$refs.salaryObserver) {
        this.$refs.salaryObserver.reset();
      }
    }
  },
  methods: {
    close() {
      this.dialog = false;
    },
    cancel() {
      this.$emit("edit-cancelled");
      this.close();
    },
    async save() {
      const isDataValid = await this.$refs.salaryObserver.validate();

      if (!isDataValid) {
        this.showValidationErrorSnackbar(this.$refs.salaryObserver);
        return;
      }

      if (this.currentIndex >= 0) {
        this.$emit("item-changed", {
          claim: this.currentItem,
          index: this.currentIndex
        });
      } else {
        this.$emit("new-item-added", this.currentItem);
      }

      this.close();
    },
    filterEnabledSalaryTypes(conf) {
      return conf["salaryRates"]["salaryRateTypes"].filter(x => x.enabled);
    },
    updateClaimAmount(claim) {
      claim.amount = calculateSalaryClaimTotal(claim);
    }
  }
};
</script>
